import { groupBy, sum } from 'lodash';
import { DevelopmentArea } from 'domains/signup/screener/data/types';
import { TypeScreenerQuestionV2, TypeScreenerQuestionV2Fields, TypeTherapyScreenerV2 } from 'types/contentful';
import { TherapyTarget } from 'domains/signup/data/types';
import { stringifyScreenerResponses } from 'domains/signup/data/leads';

export type ScreenerAreaGrade = 'on-track' | 'support-needed';

/** NOTE(juan): scoring system:
 * Each correct answer sums 0
 * "Sometimes" answers sum 0.5 (the correct answer is always on the extremes, either "always" or "never")
 * An incorrect answer (other than "sometimes") sums 1
 *
 * If the total score is above the threshold, an eval is recommended
 */

/** Minimum score required to recommend an evaluation */
const RECOMMEND_EVAL_SCORE_THRESHOLD = 1;

const SOMETIMES_ANSWER = 'Sometimes';

type DevelopmentalAreaResults = {
  area: DevelopmentArea;
  result: ScreenerAreaGrade;
};

type QuestionResult = {
  question: TypeScreenerQuestionV2Fields;
  response: string;
  result: ScreenerAreaGrade;
  /** 0 to 1 */
  questionScore: number;
};

export type ScreenerResults = {
  /** The age range title of the screener (ex: birth to 8 months) */
  screenerAgeRangeTitle: string;

  /** The type of patient who took the screener */
  targetPatient: TherapyTarget;

  /** Results by area */
  areas: DevelopmentalAreaResults[];
  /** Results by question */
  questions: QuestionResult[];
  /** True if an evaluation is recommended */
  recommendEvaluation: boolean;

  /** Results compressed down to a string */
  communicationConcerns: string;

  /** The final score sum of all questions */
  finalScore: number;
};

const getQuestionResults = (questions: TypeScreenerQuestionV2[], answers: string[]): QuestionResult[] =>
  questions.map((question, index) => {
    const {
      fields: { targetMilestoneAnswer },
    } = question;
    const answer = answers[index];

    let score: number;
    if (answer === targetMilestoneAnswer) {
      score = 0;
    } else if (answer === SOMETIMES_ANSWER) {
      score = 0.5;
    } else {
      score = 1;
    }

    return {
      question: question.fields,
      response: answer,
      result: score === 0 ? 'on-track' : 'support-needed',
      questionScore: score,
    };
  });

const getResultsByArea = (questionResults: QuestionResult[]): DevelopmentalAreaResults[] => {
  // @note(juan): lodash's _.groupBy doesn't have proper typescript support
  const resultsByArea = groupBy(questionResults, result => result.question.developmentArea) as unknown as Record<
    DevelopmentArea,
    QuestionResult[]
  >;

  return Object.entries(resultsByArea).map(([area, results]) => ({
    area: area as DevelopmentArea,
    result: results.some(r => r.result === 'support-needed') ? 'support-needed' : 'on-track',
  }));
};

export const calculateScreenerResults = (
  screener: TypeTherapyScreenerV2,
  answers: string[],
  target: TherapyTarget,
): ScreenerResults => {
  const questionResults = getQuestionResults(screener.fields.questions, answers);

  const areas = getResultsByArea(questionResults);

  const finalScore = sum(questionResults.map(({ questionScore }) => questionScore));

  const result: ScreenerResults = {
    screenerAgeRangeTitle: screener.fields.ageRangeTitle,
    targetPatient: target,
    questions: questionResults,
    areas,
    recommendEvaluation: finalScore >= RECOMMEND_EVAL_SCORE_THRESHOLD,
    finalScore,
    communicationConcerns: stringifyScreenerResponses(screener, answers),
  };

  return result;
};
